import React, { useState } from 'react'
import { HelpCircle, X } from 'lucide-react'

interface TourContext {
  activeTab: string
  trustCenterSubTab?: string
  requestsSubTab?: string
  knowledgeBaseTab?: string
  caiqSubTab?: string
  analyticsSubTab?: string
  settingsSubTab?: string
  tprmHash?: string
}

interface TourStarterProps {
  context: TourContext
  onStartTour: (stepIndex: number) => void
}

const TourStarter: React.FC<TourStarterProps> = ({ context, onStartTour }) => {
  const [showTooltip, setShowTooltip] = useState(false)

  // Map current context to appropriate tour step and description
  const getTourInfo = (): { step: number; title: string; description: string } => {
    const { activeTab, trustCenterSubTab, requestsSubTab, knowledgeBaseTab, caiqSubTab, analyticsSubTab, settingsSubTab, tprmHash } = context

    // Dashboard
    if (activeTab === 'dashboard') {
      return {
        step: 0,
        title: 'Start Admin Overview Tour',
        description: 'Learn how to navigate and manage your trust center admin panel'
      }
    }

    // Trust Center
    if (activeTab === 'trust-center') {
      if (trustCenterSubTab === 'accounts') {
        return {
          step: 10,
          title: 'Learn About Account Management',
          description: 'Manage user accounts, access requests, and allowlist for your Trust Center'
        }
      }
      if (trustCenterSubTab === 'deployments') {
        return {
          step: 4,
          title: 'Learn About Deployments',
          description: 'Deploy your Trust Center content changes to production'
        }
      }
      if (trustCenterSubTab === 'documents') {
        return {
          step: 2,
          title: 'Learn About Documents',
          description: 'Upload and manage security and compliance documents'
        }
      }
      if (trustCenterSubTab === 'features') {
        return {
          step: 2,
          title: 'Learn About Trust Center Features',
          description: 'Configure subprocessors, security features, compliance badges, legal pages, privacy settings, and changelog'
        }
      }
      if (trustCenterSubTab === 'settings') {
        return {
          step: 30,
          title: 'Learn About Trust Center Configuration',
          description: 'Configure company information, branding, and system-wide preferences'
        }
      }
      return {
        step: 1,
        title: 'Start Trust Center Tour',
        description: 'Learn how to build and publish your trust center'
      }
    }

    // Requests
    if (activeTab === 'requests') {
      if (requestsSubTab === 'privacy') {
        return {
          step: 12,
          title: 'Learn About Privacy Requests',
          description: 'Handle GDPR, CCPA, and privacy-related requests'
        }
      }
      return {
        step: 10,
        title: 'Learn About Access Requests',
        description: 'Manage visitor access requests and allowlists'
      }
    }

    // Knowledge Base
    if (activeTab === 'knowledge-base') {
      if (knowledgeBaseTab === 'query') {
        return {
          step: 14,
          title: 'Learn About Knowledge Base Query',
          description: 'Use AI to search your security documentation'
        }
      }
      if (knowledgeBaseTab === 'questionnaire') {
        return {
          step: 15,
          title: 'Learn About Questionnaire Upload',
          description: 'Upload and auto-complete security questionnaires'
        }
      }
      if (knowledgeBaseTab === 'caiq') {
        if (caiqSubTab === 'configure') {
          return {
            step: 16,
            title: 'Learn About CAIQ Configuration',
            description: 'Configure AI-powered CAIQ scanning sources'
          }
        }
        if (caiqSubTab === 'scan') {
          return {
            step: 17,
            title: 'Learn About CAIQ Scanning',
            description: 'Extract answers from your security documents'
          }
        }
        if (caiqSubTab === 'review') {
          return {
            step: 18,
            title: 'Learn About Answer Review',
            description: 'Review and approve AI-generated CAIQ answers'
          }
        }
        if (caiqSubTab === 'browse') {
          return {
            step: 19,
            title: 'Learn About CAIQ Browser',
            description: 'Explore all 286 CAIQ v4 questions by domain'
          }
        }
        if (caiqSubTab === 'analytics') {
          return {
            step: 20,
            title: 'Learn About KB Analytics',
            description: 'Track knowledge base completion and quality'
          }
        }
        return {
          step: 16,
          title: 'Start CAIQ Tour',
          description: 'Learn the complete CAIQ workflow'
        }
      }
      return {
        step: 13,
        title: 'Start Knowledge Base Tour',
        description: 'Discover AI-powered security questionnaire tools'
      }
    }

    // TPRM
    if (activeTab === 'tprm') {
      if (tprmHash?.includes('dashboard')) {
        return {
          step: 22,
          title: 'Learn About TPRM Dashboard',
          description: 'View your vendor risk landscape overview'
        }
      }
      if (tprmHash?.includes('assessments')) {
        return {
          step: 23,
          title: 'Learn About Vendor Assessments',
          description: 'Submit and review AI-powered vendor security assessments'
        }
      }
      if (tprmHash?.includes('settings')) {
        return {
          step: 24,
          title: 'Learn About TPRM Configuration',
          description: 'Configure risk policies and classification rules'
        }
      }
      return {
        step: 21,
        title: 'Start TPRM Tour',
        description: 'Learn vendor risk assessment and management'
      }
    }

    // Analytics
    if (activeTab === 'analytics') {
      if (analyticsSubTab === 'engagement') {
        return {
          step: 27,
          title: 'Learn About Engagement Scoring',
          description: 'Track prospect engagement and identify hot leads'
        }
      }
      if (analyticsSubTab === 'audit') {
        return {
          step: 28,
          title: 'Learn About Audit Trail',
          description: 'View complete activity logs for compliance'
        }
      }
      if (analyticsSubTab === 'revenue') {
        return {
          step: 29,
          title: 'Learn About Revenue Attribution',
          description: 'Connect trust center engagement to revenue outcomes'
        }
      }
      return {
        step: 25,
        title: 'Start Analytics Tour',
        description: 'Discover powerful trust center insights'
      }
    }

    // Settings
    if (activeTab === 'settings') {
      if (settingsSubTab === 'billing') {
        return {
          step: 32,
          title: 'Learn About Billing',
          description: 'Manage subscription plan and billing information'
        }
      }
      if (settingsSubTab === 'team') {
        return {
          step: 33,
          title: 'Learn About Team Management',
          description: 'Manage team members and access permissions'
        }
      }
      if (settingsSubTab === 'integrations') {
        return {
          step: 34,
          title: 'Learn About Integrations',
          description: 'Connect external tools and manage API access'
        }
      }
      if (settingsSubTab === 'security') {
        return {
          step: 35,
          title: 'Learn About Account Security',
          description: 'Enable passkeys and security features'
        }
      }
      return {
        step: 30,
        title: 'Learn About Settings',
        description: 'Configure products, domains, and system settings'
      }
    }

    // Default fallback
    return {
      step: 0,
      title: 'Start Product Tour',
      description: 'Take a guided tour of all trust center features'
    }
  }

  const tourInfo = getTourInfo()

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute bottom-full right-0 mb-2 w-72 bg-white rounded-lg shadow-xl border-2 border-blue-500 p-4 animate-in fade-in slide-in-from-bottom-2">
          <button
            onClick={() => setShowTooltip(false)}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-start gap-3">
            <div className="bg-blue-100 rounded-full p-2 flex-shrink-0">
              <HelpCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">{tourInfo.title}</h4>
              <p className="text-sm text-gray-600 mb-3">{tourInfo.description}</p>
              <button
                onClick={() => {
                  setShowTooltip(false)
                  onStartTour(tourInfo.step)
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Start Tour Here
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Help Button */}
      <button
        onClick={() => setShowTooltip(!showTooltip)}
        className="bg-blue-600 text-white rounded-full p-4 shadow-lg hover:bg-blue-700 transition-all hover:scale-110 flex items-center justify-center group"
        aria-label="Get help with this section"
      >
        <HelpCircle className="h-6 w-6" />
        <span className="absolute right-full mr-3 bg-gray-900 text-white text-xs font-medium px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          Need help? Click for a tour
        </span>
      </button>
    </div>
  )
}

export default TourStarter
